import { supabase } from '../supabaseClient'

const PAGE_SIZE: number = 10

class OutreachService {
  getPaginatedOutreach = async (
    page: number,
    term?: string,
    statusFilter?: string,
    sortOptions?: { sortField: string; sortDirection: 'asc' | 'desc' },
    emailTypeFilter?: string,
  ): Promise<{ data: any[]; count: number; totalPages: number }> => {
    try {
      const rangeStart: number = (page - 1) * PAGE_SIZE
      const rangeEnd: number = page * PAGE_SIZE - 1

      let query = supabase
        .from('outreach_drafts')
        .select(
          `*, trips!outreach_drafts_trip_uuid_fkey (trip_name, trip_type, location, start_date, end_date)`,
          { count: 'exact' },
        )
        .range(rangeStart, rangeEnd)

      if (term) {
        query = query.or(
          `recipient_name.ilike.%${term}%,recipient_email.ilike.%${term}%,destination.ilike.%${term}%`,
        )
      }

      if (statusFilter && statusFilter !== 'ALL') {
        query = query.eq('status', statusFilter)
      }

      if (emailTypeFilter && emailTypeFilter !== 'ALL') {
        query = query.eq('email_type', emailTypeFilter)
      }

      if (sortOptions?.sortField && sortOptions.sortDirection) {
        query = query.order(sortOptions.sortField, {
          ascending: sortOptions.sortDirection === 'asc',
        })
      } else {
        query = query.order('created_at', { ascending: false })
      }

      const { data, count, error } = await query

      if (error) {
        console.error('Error fetching paginated outreach drafts:', error)
        return { data: [], count: 0, totalPages: 0 }
      }

      return {
        data: data || [],
        count: count || 0,
        totalPages: Math.ceil((count || 0) / PAGE_SIZE),
      }
    } catch (error) {
      console.error('Error in paginated outreach:', error)
      return { data: [], count: 0, totalPages: 0 }
    }
  }

  getOutreachByUUID = async (uuid: string): Promise<any | null> => {
    try {
      const { data, error } = await supabase
        .from('outreach_drafts')
        .select(
          `*, trips!outreach_drafts_trip_uuid_fkey (trip_name, trip_type, location, start_date, end_date)`,
        )
        .eq('uuid', uuid)
        .single()

      if (error) {
        console.error('Error fetching outreach by uuid:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching outreach by uuid:', error)
      return null
    }
  }

  updateOutreach = async (
    uuid: string,
    payload: {
      email_subject?: string
      email_body?: string
      notes?: string
    },
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('outreach_drafts')
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq('uuid', uuid)

      if (error) {
        console.error('Error updating outreach draft:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error updating outreach draft:', error)
      return false
    }
  }

  skipOutreach = async (uuid: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('outreach_drafts')
        .update({
          status: 'SKIPPED',
          updated_at: new Date().toISOString(),
        })
        .eq('uuid', uuid)

      if (error) {
        console.error('Error skipping outreach draft:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error skipping outreach draft:', error)
      return false
    }
  }

  sendOutreach = async (
    outreachUUID: string,
    sentByUUID: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.functions.invoke('send-outreach-email', {
        body: {
          outreach_uuid: outreachUUID,
          sent_by: sentByUUID,
        },
      })

      if (error) {
        console.error('Error sending outreach email:', error)
        return { success: false, error: error.message || 'Failed to send email' }
      }

      return { success: true }
    } catch (error: any) {
      console.error('Error sending outreach email:', error)
      return { success: false, error: error.message || 'Failed to send email' }
    }
  }
  sendTestEmail = async (
    outreachUUID: string,
    testEmail: string,
    sentByUUID: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.functions.invoke('send-outreach-email', {
        body: {
          outreach_uuid: outreachUUID,
          sent_by: sentByUUID,
          test_email: testEmail,
        },
      })

      if (error) {
        console.error('Error sending test outreach email:', error)
        return { success: false, error: error.message || 'Failed to send test email' }
      }

      return { success: true }
    } catch (error: any) {
      console.error('Error sending test outreach email:', error)
      return { success: false, error: error.message || 'Failed to send test email' }
    }
  }

  getOutreachEvents = async (
    outreachUUID: string,
  ): Promise<{ status: string; events: Array<{ type: string; received_at: string }> } | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('get-outreach-events', {
        body: { outreach_uuid: outreachUUID },
      })

      if (error) {
        console.error('Error fetching outreach events:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching outreach events:', error)
      return null
    }
  }

  getGuestProfile = async (profileUUID: string): Promise<any | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('uuid, first_name, last_name, email, phone, city, photo_url, created_at')
        .eq('uuid', profileUUID)
        .single()

      if (error) {
        console.error('Error fetching guest profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching guest profile:', error)
      return null
    }
  }

  getOutreachByRecipient = async (recipientUUID: string): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('outreach_drafts')
        .select(
          `uuid, email_subject, destination, status, created_at, sent_at, trip_start_date`,
        )
        .eq('recipient_uuid', recipientUUID)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching outreach by recipient:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching outreach by recipient:', error)
      return []
    }
  }

  getSequenceDrafts = async (sequenceUuid: string): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('outreach_drafts')
        .select(
          `uuid, email_subject, email_type, sequence_step, status, scheduled_for, created_at, sent_at, confidence_score, ab_variant`,
        )
        .eq('sequence_uuid', sequenceUuid)
        .order('sequence_step', { ascending: true })

      if (error) {
        console.error('Error fetching sequence drafts:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching sequence drafts:', error)
      return []
    }
  }

  getSequenceByUuid = async (sequenceUuid: string): Promise<any | null> => {
    try {
      const { data, error } = await supabase
        .from('outreach_sequences')
        .select('*')
        .eq('uuid', sequenceUuid)
        .single()

      if (error) {
        console.error('Error fetching sequence:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error fetching sequence:', error)
      return null
    }
  }

  regenerateOutreach = async (
    outreachUUID: string,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.functions.invoke('regenerate-outreach-draft', {
        body: { outreach_uuid: outreachUUID },
      })

      if (error) {
        console.error('Error regenerating outreach draft:', error)
        return { success: false, error: error.message || 'Failed to regenerate' }
      }

      return { success: true }
    } catch (error: any) {
      console.error('Error regenerating outreach draft:', error)
      return { success: false, error: error.message || 'Failed to regenerate' }
    }
  }

  updateSequenceStatus = async (
    sequenceUuid: string,
    status: string,
  ): Promise<boolean> => {
    try {
      const updatePayload: any = { status }
      if (status === 'COMPLETED' || status === 'CANCELLED') {
        updatePayload.completed_at = new Date().toISOString()
      }

      const { error } = await supabase
        .from('outreach_sequences')
        .update(updatePayload)
        .eq('uuid', sequenceUuid)

      if (error) {
        console.error('Error updating sequence status:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error updating sequence status:', error)
      return false
    }
  }

  getGuestTrips = async (profileUUID: string): Promise<any[]> => {
    try {
      const { data, error } = await supabase
        .from('trip_users')
        .select(
          `*, trips!trip_users_trip_uuid_fkey (uuid, trip_name, trip_type, location, start_date, end_date, status)`,
        )
        .eq('user_uuid', profileUUID)
        .eq('deleted', false)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching guest trips:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error fetching guest trips:', error)
      return []
    }
  }
}

const outreachService: OutreachService = new OutreachService()

export default outreachService
